import React, { useState, useContext, useEffect } from 'react'
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import { OperatorContext } from '../../contexts/OperatorContext';
import { UserContext } from '../../contexts/UserContext';
import OperatorActionErrorModal from './OperatorActionErrorModal';
import RemoveUserDialog from './RemoveUserDialog';
import PasswordInput from '../common/PasswordInput';
import PasswordRequirements from '../common/PasswordRequirements';
import { validatePassword } from '../../utils/validatePassword';
import Loader from '../Loader';

import './CMSForm.css'
import './ManageUsersForm.css'

window.Buffer = window.Buffer || require("buffer").Buffer;

const roleArr = ['Owner', 'Admin', 'Listing Admin', 'User']
const displayRole = (role) => String(role || '').toLowerCase() === 'manager' ? 'Listing Admin' : (role || '')

const BUILDING_ORG_TYPES = ['operator', 'consultant']

const ManageUsersForm = ({ closeForm, modalOpen, organization_id, organization_type, inline }) => {
    const { getListingByOperator, operatorListings, getAdminBuildings } = useContext(OperatorContext)
    const { getOperatorUsers, operatorUsers, createOperatorUser, editOperatorUser, resetOperatorUserPassword, deleteOperatorUser, getAllUsers, editUser } = useContext(UserContext)
    
    const [selected, setSelected] = useState(null)
    const [attachExisting, setAttachExisting] = useState(false)
    const [existingUser, setExistingUser] = useState(null)
    const [userSearch, setUserSearch] = useState('')
    const [allUsers, setAllUsers] = useState([])
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [activeBuildings, setActiveBuildings] = useState([])
    const [buildingsLoading, setBuildingsLoading] = useState(false)
    const [buildingLoadError, setBuildingLoadError] = useState('')
    const buildingsEnabled = BUILDING_ORG_TYPES.includes(organization_type)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [resetState, setResetState] = useState(false)
    const [errorModal, setErrorModal] = useState(null)
    const [selectedUserForDelete, setSelectedUserForDelete] = useState(null)
    const [deleteDialogUser, setDeleteDialogUser] = useState(null)
    const [inputData, setInputData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        position: '',
        password: '',
        role: '',
        organization_listings: [],
        building_access: []
    })

    const [resetInput, setResetInput] = useState({
        password: '',
        confirm: '',
    })

    useEffect(() => {
        if ((modalOpen || inline) && organization_id) {
            let ignore = false
            getOperatorUsers(organization_id)
            getListingByOperator(organization_id)
            setActiveBuildings([])
            setBuildingLoadError('')
            // The server 403s Building reads for other org types (e.g. hospital).
            if (!buildingsEnabled) {
                setBuildingsLoading(false)
                return
            }
            setBuildingsLoading(true)
            getAdminBuildings(organization_id)
                .then(result => {
                    const list = result?.buildings || []
                    if (!ignore) setActiveBuildings(list.filter(building => building.status === 'active'))
                })
                .catch(err => {
                    if (ignore) return
                    const errorData = err.response?.data
                    setBuildingLoadError(errorData?.message || errorData?.msg || errorData?.error || 'Failed to load Buildings.')
                })
                .finally(() => {
                    if (!ignore) setBuildingsLoading(false)
                })
            return () => {
                ignore = true
            }
        }
    }, [modalOpen, inline, organization_id, buildingsEnabled])

    useEffect(() => {
        if (selected) {
            const userOrg = selected.organizations?.find(o => String(o.organization_id) === String(organization_id))
            const listingAccess = userOrg?.listing_access || []
            const buildingAccess = userOrg?.building_access || []
            setInputData({
                first_name: selected.first_name,
                last_name: selected.last_name,
                email: selected.email,
                position: selected.position || '',
                role: userOrg?.role || '',
                organization_listings: listingAccess.map(e => operatorListings.find(listing => String(listing._id) === String(e.id))?._id).filter(Boolean).map(String),
                building_access: buildingAccess.map(building => String(building?._id || building?.id || building))
            })
        }
        else {
            setInputData({
                first_name: '',
                last_name: '',
                email: '',
                position: '',
                password: '',
                role: '',
                organization_listings: [],
                building_access: [],
            })
        }
        setError('')
        setSuccess('')
        setResetState(false)
    }, [selected])

    useEffect(() => {
        if (!selected) return
        const userOrg = selected.organizations?.find(o => String(o.organization_id) === String(organization_id))
        const listingAccess = userOrg?.listing_access || []
        setInputData(prev => {
            if (prev.organization_listings.length) return prev
            return {
                ...prev,
                organization_listings: listingAccess.map(e => operatorListings.find(listing => String(listing._id) === String(e.id))?._id).filter(Boolean).map(String)
            }
        })
    }, [operatorListings])

    useEffect(() => {
        if (!selected || buildingsLoading) return
        const activeIds = new Set(activeBuildings.map(building => String(building._id)))
        setInputData(prev => ({
            ...prev,
            building_access: prev.building_access.filter(id => activeIds.has(String(id)))
        }))
    }, [activeBuildings, buildingsLoading, selected])

    function handleCloseForm(e) {
        // mousedown (not click): a failed save unmounts the spinner under the cursor,
        // and the leftover click would otherwise hit this overlay and dismiss the form.
        if (isSubmitting) return
        if (e.target === e.currentTarget) closeForm()
    }

    function handleSelect(data) {
        setAttachExisting(false)
        setSelected(data)
        setSuccess('')
    }

    function handleSelectExisting() {
        setSelected(null)
        setExistingUser(null)
        setUserSearch('')
        setAttachExisting(true)
        setError('')
        setSuccess('')
        setResetState(false)
        getAllUsers(1, undefined, 'all').then((data) => { if (data) setAllUsers(data) })
    }

    function handleUserSearch(e) {
        const value = e.target.value
        setUserSearch(value)
        getAllUsers(1, value || undefined, 'all').then((data) => { if (data) setAllUsers(data) })
    }

    async function handleAttachExisting() {
        if (!existingUser || !inputData.role) {
            setError('Select a user and role')
            return
        }
        setError('')
        setSuccess('')
        setIsSubmitting(true)
        try {
            await editUser(existingUser._id, {
                first_name: existingUser.first_name,
                last_name: existingUser.last_name,
                email: existingUser.email,
                organization_id,
                role: inputData.role,
                building_access: inputData.role === 'Owner' ? [] : inputData.building_access.map(String),
            })
            await getOperatorUsers(organization_id)
            setExistingUser(null)
            setUserSearch('')
            setSuccess('User added to the organization.')
            getAllUsers(1, undefined, 'all').then((data) => { if (data) setAllUsers(data) })
        } catch (err) {
            const errorData = err.response?.data
            setError(errorData?.errors?.length ? errorData.errors.join(' ') : errorData?.msg || errorData?.error || 'Failed to add user.')
        } finally {
            setIsSubmitting(false)
        }
    }

    async function handleClick() {
        if (!emailCheck(inputData.email)) {
            setError('Invalid Email')
            return
        }
        if (!inputData.position?.trim()) {
            setError('Position is required.')
            return
        }
        if (!selected && !validatePassword(inputData.password)) {
            setError('Password does not meet the requirements.')
            return
        }
        setError('')
        setSuccess('')
        setIsSubmitting(true)

        try {
            const userData = {
                ...inputData,
                organization_listings: inputData.organization_listings.map(e => {
                    const listing = operatorListings.find(listing => String(listing._id) === String(e))
                    return listing ? { id: listing._id } : null
                }).filter(Boolean),
                building_access: inputData.role === 'Owner' ? [] : inputData.building_access.map(String)
            }

            if (selected) {
                await editOperatorUser(
                    userData,
                    selected._id,
                    organization_id
                )
            }
            else {
                await createOperatorUser({
                    ...userData,
                    organization_id: organization_id
                })
            }
            await getOperatorUsers(organization_id)
            setSuccess('User saved.')
        } catch (err) {
            const errorData = err.response?.data
            setError(
                errorData?.errors?.length
                    ? errorData.errors.join(' ')
                    : errorData?.msg || errorData?.message || errorData?.error || 'Failed to save user.'
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    function handleChange(e) {
        const { name, value } = e.target;

        if (name === 'role') {
            const primaryBuilding = activeBuildings.find(building => building.is_primary)
            setInputData({
                ...inputData,
                role: value,
                building_access: value === 'Owner'
                    ? []
                    : inputData.building_access.length > 0
                        ? inputData.building_access
                        : primaryBuilding
                            ? [String(primaryBuilding._id)]
                            : []
            })
            return
        }

        setInputData({
            ...inputData,
            [name]: value,
        });

    }

    function handleChangePassword(e) {
        const { name, value } = e.target;

        setResetInput({
            ...resetInput,
            [name]: value,
        });
    }

    function emailCheck(email) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    async function handleReset() {
        if (!validatePassword(resetInput.password)) {
            setError('Password does not meet the requirements.')
            return
        }
        if (resetInput.password !== resetInput.confirm) {
            setError("Passwords do not match")
            return
        }

        try {
            await resetOperatorUserPassword(
                { password: resetInput.password },
                selected._id,
            )
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            setError(serverErrors?.length ? serverErrors.join(' ') : 'Failed to reset password.')
            return
        }

        setResetInput({
            password: '',
            confirm: '',
        })
        setError('')
        setResetState(false)
    }

    // Opens the confirm dialog; the dialog's buttons decide which flags handleDelete gets.
    // Clears any retry target left over from a previous failed delete so a fresh
    // confirmation can never act on the earlier user.
    function requestDelete() {
        if (!selected) return
        setError('')
        setErrorModal(null)
        setSelectedUserForDelete(null)
        setDeleteDialogUser(selected)
    }

    // `detach` is local (a PUT that drops one membership); the rest are server flags.
    async function handleDelete({ detach = false, ...additionalFlags } = {}) {
        // A freshly confirmed user wins; error-modal retries (no dialog open) fall
        // through to the target held from the attempt that failed.
        const userToDelete = deleteDialogUser || selectedUserForDelete || selected;
        if (!userToDelete) return

        setDeleteDialogUser(null)
        setSelectedUserForDelete(userToDelete)

        try {
            if (detach) {
                // Flags ride at the top level, not inside the user payload: a detach that
                // empties the org is gated server-side exactly like a delete.
                await editUser(userToDelete._id, {
                    first_name: userToDelete.first_name,
                    last_name: userToDelete.last_name,
                    email: userToDelete.email,
                    remove_organization_ids: [organization_id],
                }, undefined, additionalFlags)
                setErrorModal(null)
                setSelectedUserForDelete(null)
                setSelected(null)
                // The org is gone on that branch, so the form behind this one is stale.
                if (additionalFlags.move_and_delete_organization && !inline) {
                    closeForm()
                } else {
                    getOperatorUsers(organization_id)
                }
                return
            }

            await deleteOperatorUser(
                userToDelete._id,
                organization_id,
                additionalFlags
            )

            setErrorModal(null)
            setSelectedUserForDelete(null)
            setSelected(null)

            if (additionalFlags.move_and_delete_organization && !inline) {
                closeForm()
            } else {
                getOperatorUsers(organization_id)
            }
        } catch (error) {
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === 'organization_has_active_subscriptions' ||
                    errorData.error === 'operator_has_active_subscriptions' ||
                    errorData.error === 'require_move_and_delete_organization' ||
                    errorData.error === 'organization_has_assigned_users' ||
                    errorData.error === 'operator_has_assigned_users') {
                    setErrorModal(errorData);
                    return;
                }
            }
            setSelectedUserForDelete(null);
            const errorData = error.response?.data;
            setError(errorData?.errors?.length ? errorData.errors.join(' ') : errorData?.msg || errorData?.error || 'Failed to remove user.');
        }
    }

    const handleMoveAndDeleteOperator = () => {
        handleDelete({ move_and_delete_organization: true });
    };

    const handleRetry = handleMoveAndDeleteOperator;

    const handleRetryWithCancelSubscription = () => {
        handleDelete({ cancel_active_subscription: true, move_and_delete_organization: true });
    };

    const handleCancelErrorModal = () => {
        setErrorModal(null);
        setSelectedUserForDelete(null);
    };

    const deleteDialogIsDetach = (deleteDialogUser?.organizations || []).length > 1
    // Last member of this org: the server would otherwise force the org to be deleted.
    const deleteDialogIsLastUser = operatorUsers.length <= 1
    const isOwnerRole = inputData.role === 'Owner'
    const buildingAssignmentsUnavailable = buildingsEnabled && !isOwnerRole && (buildingsLoading || Boolean(buildingLoadError))
    const buildingAccessControl = buildingsEnabled && inputData.role && !isOwnerRole ? (
        <>
            <label htmlFor="building_access">Building Access</label>
            <FormControl fullWidth>
                <Select
                    multiple
                    className="manger-facility-input"
                    value={inputData.building_access}
                    name="building_access"
                    id="building_access"
                    onChange={(e) => handleChange(e)}
                    disabled={buildingsLoading || Boolean(buildingLoadError)}
                    inputProps={{ 'aria-label': 'Building access' }}
                >
                    {activeBuildings.map(building => (
                        <MenuItem key={String(building._id)} value={String(building._id)}>
                            {building.name}{building.is_primary ? ' (Primary)' : ''}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {buildingsLoading && <p role="status" style={{ fontSize: '12px', color: '#666' }}>Loading Buildings...</p>}
            {buildingLoadError && <p role="alert" style={{ fontSize: '12px', color: '#E23844' }}>{buildingLoadError}</p>}
            {!buildingsLoading && !buildingLoadError && activeBuildings.length === 0 && (
                <p style={{ fontSize: '12px', color: '#666' }}>No active Buildings are available.</p>
            )}
        </>
    ) : null

    const form = (
        <div className="operator-user-form" style={inline ? { width: '100%', maxWidth: 'none', padding: 0, maxHeight: 'none' } : undefined}>
            <div className="operator-user-select-column">
                {operatorUsers.map(e => {
                    return (
                        <p key={e._id} onClick={() => { handleSelect(e) }} className="operator-user-name">
                            {e.first_name} {e.last_name}
                        </p>
                    )
                })}
                <p onClick={() => { handleSelect(null) }} style={{ color: '#1976d2' }} className="operator-user-name"> New </p>
                <p onClick={handleSelectExisting} style={{ color: '#1976d2' }} className="operator-user-name"> Existing </p>
            </div>
            <div className="operator-user-input">
                <div style={{ width: '100%', paddingBottom: '0px', ...(inline ? { padding: 0, maxWidth: 'none', maxHeight: 'none' } : {}) }} className="manage-facility-form">
                    {attachExisting ?
                        <>
                            <h3 className="manage-facility-form-title">Add Existing User</h3>
                            <label htmlFor="user_search">Search Users</label>
                            <InputBase className='manger-facility-input' value={userSearch} onChange={handleUserSearch} type="text" name="user_search" id="user_search" placeholder="Search by name or email" />

                            <label htmlFor="existing_user">User</label>
                            <FormControl fullWidth>
                                <Select
                                    className="manger-facility-input"
                                    value={existingUser?._id || ''}
                                    name="existing_user"
                                    id="existing_user"
                                    onChange={(e) => setExistingUser(allUsers.find(u => String(u._id) === String(e.target.value)) || null)}
                                    inputProps={{ 'aria-label': 'Existing user' }}
                                >
                                    {allUsers
                                        .filter(u => !operatorUsers.some(ou => String(ou._id) === String(u._id)))
                                        .map(u => (
                                            <MenuItem key={u._id} value={u._id}>
                                                {u.first_name} {u.last_name} ({u.email})
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>

                            <label htmlFor="existing_user_role">Role</label>
                            <FormControl fullWidth>
                                <Select
                                    className="manger-facility-input"
                                    value={displayRole(inputData.role)}
                                    name="role"
                                    id="existing_user_role"
                                    onChange={(e) => handleChange(e)}
                                    inputProps={{ 'aria-label': 'Role for existing user' }}
                                >
                                    {roleArr.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                                </Select>
                            </FormControl>

                            {buildingAccessControl}

                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                <Button className='manger-facility-btn' disabled={isSubmitting || buildingAssignmentsUnavailable} onClick={handleAttachExisting}>{isSubmitting ? 'Submitting...' : 'Add to Organization'}</Button>
                            </div>
                            {error && <p role="alert" style={{ textAlign: 'center', margin: '5px 0px', color: '#E23844' }}>{error}</p>}
                            {success && <p role="status" style={{ textAlign: 'center', margin: '5px 0px', color: '#2E7D32' }}>{success}</p>}
                        </>
                    : !resetState ?
                        <>
                            {selected ?
                                <h3 className="manage-facility-form-title">Edit User: {selected.first_name}</h3>
                                :
                                <h3 className="manage-facility-form-title">Create New User</h3>
                            }
                            <label htmlFor="first_name">First Name</label>
                            <InputBase className='manger-facility-input' value={inputData.first_name} onChange={(e) => handleChange(e)} type="text" name="first_name" id="first_name" placeholder='User First Name' />

                            <label htmlFor="last_name">Last Name</label>
                            <InputBase className='manger-facility-input' value={inputData.last_name} onChange={(e) => handleChange(e)} type="text" name="last_name" id="last_name" placeholder='User Last Name' />

                            <label htmlFor="email">Email</label>
                            <InputBase className='manger-facility-input' value={inputData.email} onChange={(e) => handleChange(e)} type="text" name="email" id="email" placeholder='Email' />

                            <label htmlFor="position">Position</label>
                            <InputBase className='manger-facility-input' value={inputData.position} onChange={(e) => handleChange(e)} type="text" name="position" id="position" placeholder='e.g. Admissions Director' />

                            <label htmlFor="operator_user_role">Role</label>
                            <FormControl fullWidth>
                                <Select
                                    className="manger-facility-input"
                                    value={displayRole(inputData.role)}
                                    name="role"
                                    id="operator_user_role"
                                    onChange={(e) => handleChange(e)}
                                    inputProps={{ 'aria-label': 'User role' }}
                                >
                                    {roleArr.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <label htmlFor="organization_listings">Listing Access</label>
                            <FormControl fullWidth>
                                <Select
                                    multiple
                                    className="manger-facility-input"
                                    value={inputData.organization_listings}
                                    name="organization_listings"
                                    id="organization_listings"
                                    onChange={(e) => handleChange(e)}
                                    inputProps={{ 'aria-label': 'Listing access' }}
                                >
                                    {operatorListings.map(e => <MenuItem key={String(e._id)} value={String(e._id)}>{e.name}</MenuItem>)}
                                </Select>
                            </FormControl>

                            {buildingAccessControl}

                            {!selected &&
                                <>
                                    <label htmlFor="password">Password</label>
                                    <PasswordInput autoComplete="new-password" className='manger-facility-input' value={inputData.password || ''} onChange={(e) => handleChange(e)} name="password" id="password" placeholder='Password' />
                                    <PasswordRequirements password={inputData.password} />
                                </>
                            }

                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                <Button className='manger-facility-btn' disabled={isSubmitting || buildingAssignmentsUnavailable} onClick={() => handleClick()}>{isSubmitting ? 'Submitting...' : 'Save'}</Button>
                            </div>
                            {selected &&
                                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                    <Button className='manger-facility-btn' onClick={() => { setResetState(true); setError(''); }}>Reset Password</Button>
                                </div>
                            }
                            {selected &&
                                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                    <Button style={{ backgroundColor: '#E23844' }} className='manger-facility-btn' onClick={() => { requestDelete() }}>{(selected.organizations || []).length > 1 ? 'Remove' : 'Delete'}</Button>
                                </div>
                            }
                            {error && <p role="alert" style={{ textAlign: 'center', margin: '5px 0px', color: '#E23844' }}>{error}</p>}
                            {success && <p role="status" style={{ textAlign: 'center', margin: '5px 0px', color: '#2E7D32' }}>{success}</p>}
                        </>
                        :
                        <>
                            <h3 className="manage-facility-form-title">Reset Password For: {selected.first_name}</h3>

                            <label htmlFor="password">Password</label>
                            <PasswordInput autoComplete="new-password" className='manger-facility-input' value={resetInput.password} onChange={(e) => handleChangePassword(e)} name="password" id="password" placeholder='New Password' />
                            <PasswordRequirements password={resetInput.password} />

                            <label htmlFor="confirm">Confirm Password</label>
                            <PasswordInput autoComplete="new-password" className='manger-facility-input' value={resetInput.confirm} onChange={(e) => handleChangePassword(e)} name="confirm" id="confirm" placeholder='Confirm Password' />

                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                <Button className='manger-facility-btn' onClick={() => handleReset()}>reset</Button>
                            </div>
                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '10px' }}>
                                <Button className='manger-facility-btn' onClick={() => { setResetState(false); setError(''); }}>Cancel</Button>
                            </div>
                            <p style={{ textAlign: 'center', margin: '5px 0px' }}>{error}</p>
                        </>
                    }
                </div>
            </div>
        </div>
    )

    return (
        <>
            <OperatorActionErrorModal
                error={errorModal}
                onRetry={handleRetry}
                onCancel={handleCancelErrorModal}
                onRetryWithCancelSubscription={handleRetryWithCancelSubscription}
                onMoveAndDeleteOperator={handleMoveAndDeleteOperator}
            />
            <RemoveUserDialog
                user={deleteDialogUser}
                isDetach={deleteDialogIsDetach}
                isLastUser={deleteDialogIsLastUser}
                onCancel={() => setDeleteDialogUser(null)}
                onDetach={() => handleDelete({ detach: true })}
                onDetachKeepOrganization={() => handleDelete({ detach: true, keep_organization: true })}
                onDetachWithOrganization={() => handleDelete({ detach: true, move_and_delete_organization: true })}
                onDelete={() => handleDelete()}
                onDeleteKeepOrganization={() => handleDelete({ keep_organization: true })}
                onDeleteWithOrganization={() => handleDelete({ move_and_delete_organization: true })}
            />
            {inline ? form : (
                <div onMouseDown={handleCloseForm} className="modal-overlay">
                    {form}
                </div>
            )}
            {isSubmitting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                    flexDirection: 'column',
                    gap: '20px'
                }}>
                    <Loader loading={true} height={60} />
                    <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                        Submitting form...
                    </div>
                </div>
            )}
        </>
    )
}

export default ManageUsersForm
